import {
	Channel,
	ChatMessageFormat,
	CreateReason,
	Deferred,
	LegState,
	Log,
	Logger,
	MethodType as MT,
	RequestMessage,
	ResponseMessage,
	ResultCode,
	EventBusInstance
} from "@sap/cpaas-commlib-lib";
import {Type, Validate} from '@sap/cpaas-commlib-lib/src/constants';
import {AppState, Configuration} from './configuration';
import {MessageProvider, MessageType} from "./messageProvider";
import {guid} from '../utils/util';

export class EcfAdapter {

	private static instance: EcfAdapter;
	commlibSocket: Socket;
	url: string;

	private ecfSocket: WebSocket;
	private ecfSessionId: string;
	ecfAuthId: string;
	private ecfHeartBeat: any;
	private ecfMessages: { [reqId: string]: ECFReqMessage } = {};
	private ecfSubscriptions: { [subId: string]: ECFRespMessage } = {};
	private ecfRTCState: EcfRTCState = EcfRTCState.Off;

	private requests: any = [];
	private log = new Log("ECFAdapter");

	private originator: string;
	private interactionData: any;
	private participants: { [address: string]: string } = {};

	// sync flags
	private visitorEnd: boolean = false;
	private visitorStart: boolean = false;
	private processLock: boolean = false;

	/** @hidden */
	static getInstance() {
		return EcfAdapter.instance;
	}

	constructor(url: string) {
		if (EcfAdapter.instance) {
			EcfAdapter.instance.url = url;
			return EcfAdapter.instance;
		}
		EcfAdapter.instance = this;
		this.url = url;
		this.originator = this.genOriginator();
	}

	async init() {
		if (!await this.authenticate()) {
			this.log.err("init", "no cred");
			this.commlibSocket.callback("onclose", new SocketEvent(EventType.CloseEvent, "auth", { wasClean: false, reason: 1006 }));
			return;
		}

		let wsUrl = this.url + "/ws_endpoint/";
		if (this.ecfAuthId) {
			wsUrl += ";jsessionid=" + this.ecfAuthId;
		}
		// Reusing SUBSCRIPTIONS can lead to "dead" subs => ignore sessionId reuse for now
		// if (this.ecfSessionId) {
		// 	wsUrl += "?sessionid=" + this.ecfSessionId;
		// }
		this.log.dbg("init", "Open ECF ws", wsUrl);
		try {
			this.ecfSocket = new WebSocket(wsUrl);
		} catch (e) {
			this.log.err("init", e);
			this.commlibSocket.callback("onclose", new SocketEvent(EventType.CloseEvent, "open", { wasClean: false, reason: 1006 }));
			return;
		}
		this.ecfSocket.addEventListener("close", this.onEcfSocketClose.bind(this));
		this.ecfSocket.addEventListener("message", this.onEcfSocketMessage.bind(this));
		this.ecfSocket.addEventListener("error", this.onEcfSocketError.bind(this));
	}

	async close(code = 1000) {
		const fn = "close";
		this.log.dbg(fn, code);
		// Stop heartbeat timer
		this.stopHeartbeat();
		if (this.ecfSocket && this.ecfSocket.readyState === SocketState.Open) {
			// unsub
			this.log.dbg(fn, "close ECF connection");
			if (code === 1000) {
				for (const key in this.ecfSubscriptions) {
					await this.sendToEcf(MethodType.Unsubscribe, this.ecfSubscriptions[key].uri);
				}
			}
			this.ecfSocket.close();
		}
		this.ecfSubscriptions = {};
		for (const key in this.ecfMessages) {
			// reject all the pending requests
			this.ecfMessages[key].deferred.rej(ResultCode.NotFound);
		}
		this.ecfMessages = {};
		if (this.commlibSocket.readyState === SocketState.Open) {
			this.log.dbg(fn, "close Commlib connection");
			this.commlibSocket.readyState = SocketState.Closed;
			// Commlib will handle reconnect
			this.commlibSocket.callback("onclose", new SocketEvent(EventType.CloseEvent, "close", { wasClean: code === 1000, code, reason: code }));
		}
	}

	async authenticate(): Promise<boolean> {
		if (this.getLocalStorage("cvc_authid") && this.getLocalStorage("cvc_authid") !== this.ecfAuthId) {
			// Reuse jsessionid from localstorage. We have to give preference to localstorage, because there might be another
			// client that has successfully connected with that and using local one would lead to two separate sessions.
			// Client will subscribe on connect and compare what server gives to current state => will disconnect if the
			// interactions don't match
			this.ecfAuthId = this.getLocalStorage("cvc_authid");
		}
		let url = this.url.replace("wss://", "https://").replace("ws://", "http://");
		// add the jsessionid auth request in NOCOOKIE => failure will mean that the authentication is expired
		url += "/authentication" + (this.ecfAuthId ? ";jsessionid=" + this.ecfAuthId : "");
		try {
			const resp = await fetch(url, {
				method: "POST",
				cache: "reload",
				headers: { "Authorization": "Anonymous" },
				credentials: "include",
			});
			const body = await resp.json();
			if (resp.status === 200 && body.id !== undefined) {
				// look for auth header in case the cookie is not present
				const header = resp.headers.get("X-JSessionId");
				if (header && header !== "null") {
					this.ecfAuthId = header;
					this.setLocalStorage("cvc_authid", header);
				}
				return true;
			} else {
				// authentication not valid => remove
				this.removeAuthentication();
				await this.logOut();
			}
		} catch (e) {
			// when network is down we just retry without removing auth
			this.log.err("authenticate", e);
		}
		return false;
	}

	/**
	 * Clears local NOCOOKIE authentication.
	 */
	removeAuthentication() {
		this.log.dbg("removeAuthentication");
		this.setLocalStorage("cvc_authid", "")
		this.ecfAuthId = null;
	}

	/**
	 * Clears local session.
	 */
	removeSession() {
		this.log.dbg("removeSession");
		this.ecfSessionId = null;
	}

	// removing session causes unseen issues in chat server and multiple clients
	// => disable for now (this means client can go to hard lock if server does not accept sessionid)
	// const url = this.url.replace("wss://", "https://").replace("ws://", "http://");
	// try {
	// 	const resp = await fetch(url + "/authentication", {
	// 		method: "DELETE",
	// 		cache: "reload",
	// 		headers: { "Authorization": "Anonymous" },
	// 		credentials: "include"
	// 	});
	// 	if (resp.status === 200) {
	// 		return true;
	// 	}
	// } catch (e) {
	// 	this.log.err("removeAuthentication", e);
	// }
	// return false;

	private onEcfSocketClose(e: CloseEvent) {
		this.ecfSocket.removeEventListener("close", this.onEcfSocketClose.bind(this));
		this.ecfSocket.removeEventListener("message", this.onEcfSocketMessage.bind(this));
		this.ecfSocket.removeEventListener("error", this.onEcfSocketError.bind(this));
		this.log.dbg("onEcfSocketClose", e.reason);
		// commlib will handle reconnecting
		this.close(e.code);
	}

	private onEcfSocketMessage(e: MessageEvent) {
		const fn = "onEcfSocketMessage";
		let data: ECFRespMessage;
		try {
			this.log.trc(fn, e.data);
			data = JSON.parse(e.data);
		} catch (error) {
			this.log.err(fn, "parse data", error);
			return;
		}
		if ((data as any).ecfsessionid && data.sessionid) {
			// Message with sessionId => this is the first msg from ECF
			this.ecfSessionId = data.sessionid;
			this.commlibSocket.readyState = SocketState.Open;
			this.commlibSocket.callback("onopen", new SocketEvent(EventType.Event, "open"));
			this.startHeartbeat();
			return;
		} else if (data.status_code === 403) {
			// ECF refuses to open socket with this session ID => remove this sessionid
			this.removeSession();
			this.close(1000);
		} else if (this.ecfMessages.hasOwnProperty(data.client_req_id)) {
			// resolve / reject the pending ECF request
			const msg = this.ecfMessages[data.client_req_id];
			if (data.status_code === 200) {
				msg.deferred.res(data);
				if (data.body?.transactions[0]?.subscribed_resource?.id) {
					AppState.visitorUserId = data.body.transactions[0].subscribed_resource.id;
				}
			} else {
				this.log.wrn(fn, "server rejected with %s for message %s", data.status_code, data.client_req_id);
				msg.deferred.rej(data.status_code);
			}
			delete this.ecfMessages[data.client_req_id];
		} else {
			// Subscription update => parse the message contents to decide what to do
			this.parseSubscriptionUpdate(data);
		}
	}

	private onEcfSocketError(e: SocketEvent) {
		this.log.wrn("onEcfSocketError");
	}

	private startHeartbeat() {
		this.log.dbg("startHeartbeat");
		clearInterval(this.ecfHeartBeat);
		this.ecfHeartBeat = setInterval(() => this.sendHeartbeatToEcf(), 10000);
	}

	private stopHeartbeat() {
		this.log.dbg("stopHeartbeat");
		clearInterval(this.ecfHeartBeat);
	}

	async sendHeartbeatToEcf(): Promise<any> {
		const req: ECFReqMessage = new ECFReqMessage(MethodType.Put, guid(), "wsactions", this.ecfSessionId, {}, "heartbeat");
		try {
			this.ecfSocket.send(req.print());
		} catch (error) {
			this.log.err("sendHeartbeatToEcf", error);
		}
	}

	/**
	 * Sends a message to ECF and waits for the response to it.
	 * @throws Will throw with the HTTP error code only, so it can easily be translated to commlib!
	 */
	async sendToEcf(method: MethodType, uri: string, body?: any): Promise<ECFRespMessage> {
		const fn = "sendToEcf";
		const requestId = guid();
		const req = new ECFReqMessage(method, requestId, uri, this.ecfSessionId, body);
		this.ecfMessages[requestId] = req;
		try {
			const msg = req.print();
			this.log.trc(fn, msg);
			this.ecfSocket.send(msg);
		} catch (error) {
			this.log.err(fn, "send to socket");
			throw new Error(ResultCode.BadRequest.toString());
		}
		// start the timer to a single message => reject in 10s if no response
		setTimeout(() => {
			if (!req.deferred.resolved) {
				this.log.wrn(fn, "timeout", requestId);
				req.deferred.rej(ResultCode.NotFound);
				delete this.ecfMessages[requestId];
			}
		}, 15000);

		const resp: ECFRespMessage = await req.deferred.promise;
		return resp;
	}

	/**
	 * Logs out (anonymously) authenticated visitor user, invalidating jsessionid and cookie on backend.
	 */
	private async logOut() {
		let url = this.url.replace("wss://", "https://").replace("ws://", "http://");
		// add the jsessionid auth request in NOCOOKIE => failure will mean that the authentication is expired
		url += "/authentication" + (this.ecfAuthId ? ";jsessionid=" + this.ecfAuthId : "");
		try {
			const resp = await fetch(url, {
				method: "DELETE",
				cache: "reload",
				headers: { "Authorization": "Anonymous" },
				credentials: "include",
			});
			this.log.dbg("logOut", resp.status);
			if (resp.status === 200) {
				this.log.dbg("logOut", "Logged out");
			} else if (resp.status == 403) {
				this.log.dbg("logOut", "Already logged out"); // There was another tab which already called DELETE
			} else {
				this.log.err("logOut", "Logging out failed");
			}
			return true;
		} catch (e) {
			// when network is down we just retry without removing auth
			this.log.err("logOut", e);
			return false;
		}
	}

	/**
	 * Parses ECF subscription messages to Commlib actions
	 * Does not throw errors
	 */
	private async parseSubscriptionUpdate(data: any) {
		const fn = "parseSubscriptionUpdate";
		if (data.body && data.body.transactions) {
			for (const res of data.body.transactions) {
				while (this.processLock) {
					// some Commlib processing is pending in a way that can break the system => don't process any more messages until it is complete
					await new Promise((res) => setTimeout(res, 100));
				}
				const body = res.subscribed_resource;
				switch (this.cleanECFPath(res.uri)) {
					case "/users/me/interactions/{id}":
					case "/users/me/interactions":
						if (res.method === "DELETE") {
                            // when there is no interaction, there should be no session storage
                            MessageProvider.sendMessage(MessageType.active, false);
							if (!this.visitorEnd) {
								// end chat from agent side if visitor has not requested the ending
								this.log.dbg(fn, "server ended %s", this.interactionData.id);
								await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.completed));
							}
							this.visitorEnd = false;
							// log out to ensure jsessionid is invalidated - next possible chat from this browser should get new jsessionid upon calling POST /authentication
							this.removeAuthentication();
							await this.logOut();
							break;
						}
						let interaction = body;
						this.interactionData = interaction;
						if (Array.isArray(interaction)) {
							if (interaction.length === 0) {
								break;
							}
							// recovery scenario => send new leg to visitor
							// we need to delay the leg creation, because commlib does not have the device ready when ECF sends interactions
							interaction = interaction[0];
							this.interactionData = interaction;
							setTimeout(() => {
								this.log.dbg(fn, "recovering interaction %s", interaction.id);
								this.recoverInteraction(interaction);
							});
						} else if (!this.visitorStart) {
							// This interaction is started in other terminal => push new leg to commlib
							this.log.dbg(fn, "resuming interaction from other terminal %s", interaction.id);
							await this.recoverInteraction(interaction);
						} else {
							// Interaction created and queued from this terminal. Send the queue leg to commlib after we have the visitor leg created
							// we need to delay the leg creation, because commlib does not have the leg ready when ECF sends interactions
							setTimeout(async () => {
								this.log.dbg(fn, "added interaction %s", interaction.id);
								const queueLeg = {
									id: 301,
									address: AppState.selectedQueue,
									createReason: CreateReason.Queue,
								};
                                await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/connector", interaction.id));
                                await this.sendMessage(new RequestMessage(MT.Post, "/devices/100/legs/300/contact/legs", queueLeg));
								await this.recoverMessages(interaction.transcript.messages, interaction.originator);
								MessageProvider.sendMessage(MessageType.event, {event: "chatState", carry: LegState[LegState.proceeding]});
							},100);
						}
						// when we have interaction we have a proceeding chat => set the session storage to indicate that
						MessageProvider.sendMessage(MessageType.active, true);
						this.visitorStart = false;
						break;
					case "/users/me/interactions/{id}/status":
						switch (body) {
							case "accepted":
							// agent picked the interaction => add agent leg and then remove queue leg
							/*this.log.dbg(fn, "agent accepted %s", this.interactionData.id);
							const agentLeg = {
								id: 302,
								connector: 102,
								address: this.agentName,
								user:guid(),
								createReason: CreateReason.Offer,
							};
							await this.sendMessage(new RequestMessage(MT.Post, "/devices/100/legs/300/contact/legs/302", agentLeg));
							// set legstate as connected now
							await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.connected));
							// remove queue leg as we are connected now
							await this.sendMessage(new RequestMessage(MT.Delete, "/devices/100/legs/300/contact/legs/301"));
							this.log.dbg(fn, "interaction connected %s", this.interactionData.id);
							*/
						}
						break;
					case "/users/me/interactions/{id}/channel_status":
						switch (body) {
							case "disconnected":
								this.log.dbg(fn, "server ended interaction %s", this.interactionData.id);
								await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.disconnected));
								break;
							case "connected":
								break;
							case "transferring":
								this.processLock = true;
								this.log.dbg(fn, "server transferred interaction %s", this.interactionData.id);
								// remove current agent leg
								await this.sendMessage(new RequestMessage(MT.Delete, "/devices/100/legs/300/contact/legs/302"));
								await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.proceeding));
								this.processLock = false;
								break;
							case "queue_closed":
								this.log.dbg(fn, "queue is closed %s", this.interactionData.id);
								await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.completed));
								break;
						}
						break;
					case "/users/me/interactions/{id}/transcript/messages/{id}":
						const originator = body.originator === "" ? 301 : (body.originator === this.originator ? 300 : 302);
						const reqBody: any = {
							"order": body.order,
							"format": body.type || ChatMessageFormat.text,
							"content": body.message,
							"sender": originator,
							"time": body.timestamp
						};
						await this.sendMessage(new RequestMessage(MT.Post, "/devices/100/legs/300/contact/messages", reqBody));
						break;
					case "/users/me/interactions/{id}/participants/{id}":
						this.processLock = true;
						this.log.dbg(fn, "participant added %s", body.alias);
						// store agent alias
						this.participants[body.uniquekey] = body.alias;
						const agentLeg = {
							id: 302,
							address: body.alias,
							user: guid(),
							createReason: CreateReason.Offer,
						};
						await this.sendMessage(new RequestMessage(MT.Post, "/devices/100/legs/300/contact/legs/302", agentLeg));
						// set legstate as connected now
						await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.connected));
						// remove queue leg as we are connected now
						await this.sendMessage(new RequestMessage(MT.Delete, "/devices/100/legs/300/contact/legs/301"));
						this.log.dbg(fn, "interaction connected %s", this.interactionData.id);
						this.processLock = false;
						break;
					case "/users/me/interactions/{id}/participants/{id}/status":
						if (res.uri.split("/")[6] !== this.originator) {
							const agentName = this.participants[res.uri.split("/")[6]];

							switch (body) {
								case "typing":
									await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/contact/legs/302/media/typing", true));
									break;
								case "not_typing":
									await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/contact/legs/302/media/typing", false));
									break;
								case "active":
									this.processLock = true;
									// add new agent leg
									const newAgentLeg = {
										id: 302,
										address: agentName,
										user: guid(),
										createReason: CreateReason.Offer,
									};
									await this.sendMessage(new RequestMessage(MT.Post, "/devices/100/legs/300/contact/legs/302", newAgentLeg));
									// set legstate as connected now
									await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.connected));
									// remove queue leg as we are connected now
									await this.sendMessage(new RequestMessage(MT.Delete, "/devices/100/legs/300/contact/legs/301"));
									this.log.dbg(fn, "interaction connected %s", this.interactionData.id);
									this.processLock = false;
									break;
								case "inactive":
									// add queue leg
									this.processLock = true;
									const queueLeg = {
										id: 301,
										address: AppState.selectedQueue,
										createReason: CreateReason.Queue,
									};
									await this.sendMessage(new RequestMessage(MT.Post, "/devices/100/legs/300/contact/legs", queueLeg));
									// remove current agent leg
									await this.sendMessage(new RequestMessage(MT.Delete, "/devices/100/legs/300/contact/legs/302"));
									this.processLock = false;
									break;
							}
						}
						break;
					case "/users/me/interactions/{id}/webrtc/messages/{id}":
						if (body.message === "OFFER" && body.originator !== this.originator) {
							// send offer
							const reqOffer: any = {
								"sdp": res.subscribed_resource.sdp.sdp,
								"candidates": res.subscribed_resource.icecandidates,
							};
							this.log.dbg(fn, "new RDP offer from agent");
							await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/media/offer", reqOffer));
						}
						if (res.subscribed_resource.message === "ANSWER") {
							// send answer
							if (this.ecfRTCState === EcfRTCState.Signaling) {
								const reqAnswer = {
									"sdp": res.subscribed_resource.originator === this.originator ? res.subscribed_resource.sdp : res.subscribed_resource.sdp.sdp,
									"candidates": res.subscribed_resource.icecandidates,
								};
								this.log.dbg(fn, "new RDP answer from agent");
								await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/media/answer", reqAnswer));
								await this.sendToEcf(
									MethodType.Put,
									"/users/me/interactions/" + this.interactionData.id + "/webrtc",
									{
										status: "on",
									}
								);
							}
						}
						break;
					case "/users/me/interactions/{id}/webrtc/status":
						switch (body) {
							case "signaling": {
								this.ecfRTCState = EcfRTCState.Signaling;
								break;
							}
							case "on": {
								this.ecfRTCState = EcfRTCState.On;
								break;
							}
							case "off": {
								this.ecfRTCState = EcfRTCState.Off;
								break;
							}
							case "error": {
								this.ecfRTCState = EcfRTCState.Error;
								break;
							}
						}
						// await this.sendMessage(new RequestMessage(MT.Put, "/devices/{device-id}/legs/{leg-id}/media/sending", true));
						break;
				}
			}
		}
	}


	/**
	 * Sends commlib messages based on ECF interaction to initialize a new chat
	 * @param interaction
	 */
	private async recoverInteraction(interaction: any) {
		const fn = "recoverInteraction";
		const isQueued = interaction.status === "queued";
		let alias = interaction.destination;
		const legId = isQueued ? 301 : 302;
		if (!isQueued) {
			const agent = interaction.participants.find((participant: any) => participant.uniquekey !== interaction.originator);
			alias = agent.alias;
			this.participants[agent.uniquekey] = alias;
		}
		const leg: any = {
			state: isQueued ? LegState.proceeding : LegState.connected,
			id: 300,
			channel: interaction.channel_sub_type === "video" ? Channel.video : Channel.chat,
			contact: {
				id: 200,
				messages: [],
				legs: {
					[legId]: {
						id: legId,
						createReason: isQueued ? CreateReason.Queue : CreateReason.Offer,
						address: alias
					}
				}
			},
			media: { answer: {}, offer: {}, typing: false },
            connector: interaction.id
		};
		this.originator = interaction.originator;
		// Send the new agent/queue leg
		await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300", leg));
		this.log.dbg(fn, "recovered participants %s, %s", this.originator, alias);
		setTimeout(async () => {
			// give a timeout for client to render Chat screen
			this.recoverMessages(interaction.transcript.messages, interaction.originator);
		}, 1000);
	}

	/**
	 * Sends chat messags to commlib based on ECF interaction
	 * @param interactionData
	 */
	private async recoverMessages(messages: any[], interactionOriginator: string) {
		for (let i = 0; i < messages.length; i++) {
			const message = messages[i];
			// orignator is system if empty, visitor if it's the original originator or agents if nor
			const originator = message.originator === "" ? 301 : (message.originator === interactionOriginator ? 300 : 302);
			const cmMessage = {
				content: message.message,
				format: message.type || ChatMessageFormat.text,
				sender: originator,
				order: i,
				contact: 200,
				time: message.timestamp
			};
			// send the messages one by one
			await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/contact/messages", cmMessage));

			// If added message was the last one, publish event.
			if (i+1 === messages.length) {
				this.log.dbg("recoverMessages", "recovered %s messages", messages.length);
				EventBusInstance.publish(Configuration.allMessagesRecoveredEvent, undefined, Configuration.component);
			}
		}
	}

	/**
	 * Generates random Originator address
	 */
	private genOriginator() {
		const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
		const segLen = 4;
		const segments: string[] = [];
		let str = '';
		for (let s = 0; s < 4; s++) {
			segments[s] = "";
			for (let i = segLen; i > 0; i--) {
				segments[s] += chars[Math.floor(Math.random() * chars.length)];
			}
		}
		str = segments[0] + "." + segments[1] + "@" + segments[2] + "." + segments[3];
		return str;
	}

	/**
	 * Handle a message received from the client.
	 * This modifies the mock model and sends a corresponding protocol response to the client.
	 * @param {RequestMessage | ResponseMessage} message
	 */
	async receiveMessage(message: RequestMessage | ResponseMessage): Promise<void> {
		const fn = "receiveMessage";
		this.log.trc(fn, message.print() || JSON.stringify(message));
		if (message instanceof RequestMessage) {
			try {
				switch (message.method) {
					case MethodType.Get:
						this.sendMessage(this.getNode(message));
						break;
					case MethodType.Put:
						this.sendMessage(await this.putNode(message));
						break;
					case MethodType.Delete:
						this.sendMessage(this.deleteNode(message));
						break;
					case MethodType.Post:
						this.sendMessage(await this.postNode(message));
						break;
				}
			} catch (e) {
				this.sendMessage(new ResponseMessage(ResultCode.BadRequest, message.id));
			}
		} else if (message instanceof ResponseMessage) {
			// In the case of ResponseMessage we are reacting to response from the client.
			if (this.requests.hasOwnProperty(message.id)) {
				this.requests[message.id].deferred.res(this.requests[message.id]);
				delete this.requests[message.id];
			}
		}
	}

	/**
	 * Send message to the client (commlib module).
	 * In other words, fake a server originated push message.
	 * Does not throw errors as they are protocol level only!
	 * @param {ResponseMessage | RequestMessage} message Message to be sent
	 */
	async sendMessage(message: ResponseMessage | RequestMessage): Promise<any> {
		const fn = "sendMessage";
		this.log.trc(fn, message.print() || JSON.stringify(message));
		// Send message async

		if (this.commlibSocket) {
			this.commlibSocket.callback("onmessage", new SocketEvent(EventType.MessageEvent, "message", { data: message.print() || JSON.stringify(message), origin: this.url }));
		} else {
			this.log.err(fn, "Socket closed", message.id);
		}

		if (message instanceof RequestMessage && message.id !== "FF") {
			if (!message.deferred) {
				message.deferred = new Deferred();
			}
			this.requests[message.id] = message;
			return message.deferred.promise;
		}
	}

	/**
	 * Implements all the possible GET methods of the device API.
	 * @hidden
	 */
	getNode(request: RequestMessage): ResponseMessage {
		let result;
		switch (this.cleanPath(request.url)) {
			case "/user/id":
				result = 0;
				break;
		}
		return new ResponseMessage(ResultCode.Ok, request.id, result);
	}

	/**
	 * Implements all the possible PUT methods of the device API.
	 * @hidden
	 */
	async putNode(request: RequestMessage): Promise<ResponseMessage> {
		const fn = "putNode";
		switch (this.cleanPath(request.url)) {
			case "/devices/{device-id}":
				// TODO: Revocer device needed?
				break;
			case "/devices/{device-id}/legs/{leg-id}/state":
				// first lets clear active state, we don't care about server response
				MessageProvider.sendMessage(MessageType.active, false);
				// End chat
				this.visitorEnd = true;
				try {
					if (this.ecfRTCState !== EcfRTCState.Off) {
						await this.sendToEcf(MethodType.Put, "/users/me/interactions/" + this.interactionData.id + "/webrtc", { status: "off", });
					}
					if (request.body && request.body === LegState.completed) {
						await this.sendToEcf(MethodType.Put, "/users/me/interactions/" + this.interactionData.id, { status: "ended" });
					}
				} catch (error) {
					// ignore error here, we want to pretend that it is closed
				}
				this.visitorEnd = false;
				this.log.dbg(fn, "visitor ended %s", this.interactionData.id);
				return new ResponseMessage(ResultCode.Ok, request.id, LegState.completed);
			case "/devices/{device-id}/state":
				// device recovery, Commlib sets the existing device active => we need to subscribe again (if we lost subs) and recover interactions
				if (!this.ecfSubscriptions["properties"] || !this.ecfSubscriptions["interactions"]) {
					this.log.dbg(fn, "recovering device");
					try {
						if (!this.ecfSubscriptions.hasOwnProperty("properties")) {
							const resProp = await this.sendToEcf(MethodType.Subscribe, "/users/me/properties", {});
							// add subscription to map
							this.ecfSubscriptions["properties"] = resProp;
							if (Configuration.useVideo === true) {
								await this.sendToEcf(MethodType.Put, "/users/me/properties/capabilities", {video_chat: true});
							}
						}
						if (!this.ecfSubscriptions.hasOwnProperty("interactions")) {
							const resInter = await this.sendToEcf(MethodType.Subscribe, "/users/me/interactions", {});
							this.ecfSubscriptions["interactions"] = resInter;
							// recovery scenario => ECF sends back interaction inside SUBSCRIBE. Only do this if we don't have a proceeding interaction in memory
							if (AppState.activeLeg.getState() !== LegState.connected && AppState.activeLeg.getState() !== LegState.proceeding) {
								this.parseSubscriptionUpdate(resInter);
							} else {
								//check that in memory stuff is valid
								const interaction = resInter?.body?.transactions[0]?.subscribed_resource[0];
								if(!interaction){
									await this.sendMessage(new RequestMessage(MT.Put, "/devices/100/legs/300/state", LegState.completed));
								}else{
									// recover new messages
									const prevMessages = AppState.messages.map(m => m.content);
									const messages = interaction.transcript.messages.filter((m: any) => prevMessages.indexOf(m.message) === -1);
									await this.recoverMessages(messages, interaction.originator);
								}
							}
						}
						this.log.dbg(fn, "subscriptions active %s, %s", this.ecfSubscriptions["properties"].uri, this.ecfSubscriptions["interactions"].uri);
					} catch (e) {
						this.log.wrn(fn, "subscriptions failed");
						// subscription failure => ECF broken => kill the session completely
						this.removeSession();
						this.removeAuthentication();
						await this.logOut();
						setTimeout(() => this.close(1000), 100);
						return new ResponseMessage(ResultCode.InternalServerError, request.id);
					}
				}
				return new ResponseMessage(ResultCode.Ok, request.id);
			case "/devices/{device-id}/legs/{leg-id}/media/answer":
				const node = request.body;
				if (node === undefined || node === null) {
					this.sendToEcf(
						MethodType.Put,
						"/users/me/interactions/" + this.interactionData.id + "/webrtc",
						{
							status: "off",
						}
					);
				}
				this.log.dbg(fn, "new RDP answer from visitor" + "node:: " + node);
				try {
					this.sendToEcf(
						MethodType.Post,
						"/users/me/interactions/" + this.interactionData.id + "/webrtc/messages",
						{
							sdp: {
								sdp: node.sdp,
								type: "answer",
							},
							message: "ANSWER",
							originator: this.originator,
							icecandidates: node.candidates,
						}
					);
					if (this.ecfRTCState === EcfRTCState.Signaling) {
						await this.sendToEcf(
							MethodType.Put,
							"/users/me/interactions/" + this.interactionData.id + "/webrtc",
							{
								status: "on",
							}
						);
					}
				} catch (error) {
					return new ResponseMessage(error.status_code, request.id);
				}
				return new ResponseMessage(ResultCode.Ok, request.id);
			case "/devices/{device-id}/legs/{leg-id}/media/offer":
				const node1 = request.body;
				this.log.dbg(fn, "new RDP offer from visitor");
				try {
					await this.sendToEcf(MethodType.Put, "/users/me/interactions/" + this.interactionData.id + "/webrtc", { status: "signaling" }
					);
					await this.sendToEcf(
						MethodType.Post,
						"/users/me/interactions/" + this.interactionData.id + "/webrtc/messages",
						{
							sdp: {
								sdp: node1.sdp,
								type: "offer",
							},
							message: "OFFER",
							originator: this.originator,
							icecandidates: node1.candidates
						}
					);
				} catch (error) {
					return new ResponseMessage(error, request.id);
				}
				return new ResponseMessage(ResultCode.Ok, request.id);
			case "/devices/{device-id}/legs/{leg-id}/media/sending":
				await this.sendToEcf(
					MethodType.Put,
					"/users/me/interactions/" + this.interactionData.id + "/webrtc",
					{
						status: "on",
					}
				);
				return new ResponseMessage(ResultCode.Ok, request.id);
			case "/devices/{device-id}/legs/{leg-id}/media/typing":
				try {
					await this.sendToEcf(
						MethodType.Put,
						"/users/me/interactions/" + this.interactionData.id + "/participants/" + this.interactionData.originator,
						{
							status: request.body ? "typing" : "not_typing",
						}
					);
				} catch (e) {
					return new ResponseMessage(e, request.id);
				}
				return new ResponseMessage(ResultCode.Ok, request.id);
			case "/devices/{device-id}/legs/{leg-id}/contact/cad":
				const cad = request.body;
				// Update CAD on the fly doesn't work (returns 400 Bad Request)
				/*await this.sendToEcf(
					MethodType.Put,
					"/users/me/interactions/"+this.interactionData.id, {attached_data: cad}
				);*/
				return new ResponseMessage(ResultCode.Ok, request.id);
			default:
				return new ResponseMessage(ResultCode.NotFound, request.id);
		}
	}

	/**
	 * Implements all the possible POST methods of the device API.
	 * @hidden
	 */
	async postNode(request: RequestMessage): Promise<ResponseMessage> {
		const fn = "postNode";
		switch (this.cleanPath(request.url)) {
			case "/devices":
				// Update uset settings and capability first
				this.log.dbg(fn, "registering device for channel %s", request.body.channel);
				try {
					if (!this.ecfSubscriptions.hasOwnProperty("properties")) {
						const resProp = await this.sendToEcf(MethodType.Subscribe, "/users/me/properties", {});
						// add subscription to map
						this.ecfSubscriptions["properties"] = resProp;
						if (Configuration.useVideo === true) {
							await this.sendToEcf(MethodType.Put, "/users/me/properties/capabilities", { video_chat: true });
						}
						// Apply alias / address from server in a scenario where they have not been set
						const props = resProp?.body?.transactions[0]?.subscribed_resource;
						if (!Configuration.userName && props?.alias !== "visitor") {
							Configuration.userName = props.alias;
						}
						if (!Configuration.userAddress) {
							Configuration.userAddress = props.chat_address;
						}
					}
					if (!this.ecfSubscriptions.hasOwnProperty("interactions")) {
						const resInter = await this.sendToEcf(MethodType.Subscribe, "/users/me/interactions", {});
						this.ecfSubscriptions["interactions"] = resInter;
						// recovery scenario => ECF sends back interaction inside SUBSCRIBE
						this.parseSubscriptionUpdate(resInter);
					}
					this.log.dbg(fn, "subscriptions active %s, %s", this.ecfSubscriptions["properties"].uri, this.ecfSubscriptions["interactions"].uri);
				} catch (e) {
					this.log.wrn(fn, "subscriptions failed");
					// subscription failure => ECF broken => kill the session completely
					this.removeSession();
					this.removeAuthentication();
					await this.logOut();
					setTimeout(() => this.close(1000), 100);
					return new ResponseMessage(ResultCode.InternalServerError, request.id);
				}
				const visitorDevice = {
					channel: request.body.channel,
					carrier: "cctr",
					id: 100,
				};
				return new ResponseMessage(ResultCode.Created, request.id, visitorDevice);

			case "/devices/{device-id}/legs":
				this.log.dbg(fn, "start chat");
				// create our visitor leg
				const leg = {
					state: LegState.proceeding,
					id: 300,
					createReason: CreateReason.From,
					channel: request.body.channel,
					contact: { id: 200, fromLeg: 300 },
					carrier: "cctr",
				};
				this.visitorStart = true;
				try {
					this.originator = Configuration.userAddress || this.originator;
					await this.sendToEcf(
						MethodType.Put,
						"/users/me/properties",
						{
							alias: Configuration.userName || "visitor",
							chat_address: this.originator,
						}
					);
					await this.sendToEcf(
						MethodType.Post,
						"/users/me/interactions",
						{
							channel_sub_type: Configuration.useVideo === true ? "video" : "text",
							channel_type: "chat",
							destination: AppState.selectedQueue,
							attached_data: Configuration.cad,
						}
					);
				} catch (error) {
					this.log.err(fn, "start chat", error);
					// ECF broken => kill the whole session completely
					this.visitorStart = false;
					this.removeSession();
					this.removeAuthentication();
					await this.logOut();
					setTimeout(() => this.close(1011), 100);
					return new ResponseMessage(ResultCode.InternalServerError, request.id);
				}
				return new ResponseMessage(ResultCode.Created, request.id, leg);
			case "/devices/{device-id}/legs/{leg-id}/contact/cad":
				// TODO: Implement towards ECF
				return new ResponseMessage(ResultCode.Created, request.id, request.body);
			case "/devices/{device-id}/legs/{leg-id}/media/spd/candidates":
				// TODO: implement when streaming of candidates is possible from ECF
				return new ResponseMessage(ResultCode.Created, request.id, request.body);
			case "/devices/{device-id}/legs/{leg-id}/contact/messages":
				try {
					await this.sendToEcf(
						MethodType.Post,
						"/users/me/interactions/" + this.interactionData.id + "/transcript/messages",
						{
							message: request.body.content,
							originator: this.originator,
							type: request.body.format
						}
					);
				} catch (error) {
					return new ResponseMessage(error, request.id);
				}
				const message = { ...request.body, isMessageFromVisitor: true, time: (new Date().toISOString()) };
				return new ResponseMessage(ResultCode.Created, request.id, message);
			default:
				return new ResponseMessage(ResultCode.NotFound, request.id);
		}
	}

	/**
	 * Implements all the possible DELETE methods of the device API.
	 * @hidden
	 */
	deleteNode(request: RequestMessage): ResponseMessage {
		const fn = "deleteNode";
		// TODO: see commlib for DELETE req
		switch (this.cleanPath(request.url)) {
			case "/devices/{device-id}/legs/{leg-id}/media/answer":
				this.sendToEcf(
					MethodType.Put,
					"/users/me/interactions/" + this.interactionData.id + "/webrtc",
					{
						status: "off",
					}
				);
				return new ResponseMessage(ResultCode.Ok, request.id);
			default:
				return new ResponseMessage(ResultCode.Ok, request.id);
		}
	}

	private cleanECFPath(path: string): string {
		const parts = path.split("/");
		for (let i = 1; i < parts.length; i++) {
			switch (parts[i - 1]) {
				case "interactions":
				case "properties":
				case "participants":
				case "messages":
					parts[i] = "{id}";
					break;
			}
		}
		return parts.join("/");
	}

	private cleanPath(path: string): string {
		const parts = path.split("/");
		for (let i = 1; i < parts.length; i++) {
			switch (parts[i - 1]) {
				case "devices":
					parts[i] = "{device-id}";
					break;
				case "legs":
					if (parts[i - 2] === "contact") {
						parts[i] = "{other-leg-id}";

					} else {
						parts[i] = "{leg-id}";
					}
					break;
			}
		}
		return parts.join("/");
	}

	private getLocalStorage(key: string) {
		try {
			return localStorage.getItem(key);
		} catch (e) {
			this.log.wrn("getLocalStorage", "storage access");
		}
	}

	private setLocalStorage(key: string, value: string) {
		try {
			localStorage.setItem(key, value);
		} catch (e) {
			this.log.wrn("setLocalStorage", "storage access");
		}
	}

}

/**
 * Mock implementation of WebSocket client.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket
 * @hidden
 */
export class Socket {
	readyState: SocketState = SocketState.Closed;
	protocol: string;
	binaryType: string = "blob";
	bufferedAmount: number = 0;
	private adapter: EcfAdapter;
	private heartBeat: any;

	constructor(url: string) {
		this.adapter = new EcfAdapter(url);
		this.adapter.commlibSocket = this;
		Logger.debug("Socket::New connection created");
		this.adapter.init();
		this.heartBeat = setInterval(() => {
			if (this.readyState === SocketState.Open) {
				this.callback("onmessage", new SocketEvent(EventType.MessageEvent, "message", { data: "ping", origin: url }));
			}
		}, 10000);
	}

	send(message: string): void {
		let request: RequestMessage | ResponseMessage;
		try {
			if (message === "pong") {
				return;
			}
			const parsedMessage: any = JSON.parse(message);
			if (parsedMessage.result) {
				request = new ResponseMessage(parsedMessage.result, parsedMessage.id, parsedMessage.body, parsedMessage.headers);
			} else if (parsedMessage.url && parsedMessage.method) {
				request = new RequestMessage(parsedMessage.method, parsedMessage.url, parsedMessage.body, parsedMessage.id, parsedMessage.headers);
			} else {
				Logger.error("Socket::send: malformed protocol message!");
				return;
			}
		} catch (e) {
			Logger.error("Socket::send: malformed protocol message!");
			return;
		}
		this.adapter.receiveMessage(request);
	}

	close(code?: number, reason?: string): void {
		clearInterval(this.heartBeat);
		code = code ? code : 1000;
		Logger.debug("Socket::close: Closing socket", String(code || ""));
		this.adapter.close(code);
	}

	onmessage(event: MessageEvent | SocketEvent): void {
		// STUB
	}

	onclose(event: CloseEvent | SocketEvent): void {
		// STUB
	}

	onerror(event: Event | SocketEvent): void {
		// STUB
	}

	onopen(event: Event | SocketEvent): void {
		// STUB
	}

	addEventListener(eventId: string, handler: any): void {
		switch (eventId) {
			case "open":
				this.onopen = handler;
				break;
			case "close":
				this.onclose = handler;
				break;
			case "error":
				this.onerror = handler;
				break;
			case "message":
				this.onmessage = handler;
				break;
		}
	}

	removeEventListener(eventId: string, handler: any): void {
		switch (eventId) {
			case "open":
				this.onopen = (handler === this.onopen ? undefined : this.onopen);
				break;
			case "close":
				this.onclose = (handler === this.onclose ? undefined : this.onclose);
				break;
			case "error":
				this.onerror = (handler === this.onerror ? undefined : this.onerror);
				break;
			case "message":
				this.onmessage = (handler === this.onmessage ? undefined : this.onmessage);
				break;
		}
	}

	callback(callback: string, event?: any) {
		setTimeout(() => {
			if ((this as any)[callback] !== undefined) {
				(this as any)[callback](event || {});
			}
		});
	}
}

/**
 * Custom event type to support IE11
 * @hidden
 */
class SocketEvent {
	constructor(event: EventType, description: string, properties?: any, params?: any) {
		let evt: any;
		params = params || {};
		try {
			evt = new CustomEvent("test");
			switch (event) {
				case EventType.CloseEvent:
					return new CloseEvent(description, properties);
				case EventType.MessageEvent:
					return new MessageEvent(description, properties);
				case EventType.Event:
					return new Event(description, properties);
				case EventType.CustomEvent:
					return new CustomEvent(description, properties);
			}
			return evt;
		} catch (e) {
			evt = document.createEvent("CustomEvent");
			evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
			evt = { ...evt, ...properties };
			return evt;
		}
	}
}

/** @hidden */
enum EventType {
	MessageEvent = "MessageEvent", CloseEvent = "CloseEvent", CustomEvent = "CustomEvent", Event = "Event",
}

enum SocketState {
	Connecting = 0,
	Open = 1,
	Closing = 2,
	Closed = 3,
}

export class ECFMessage {

	client_req_id: string;
	uri: string;
	body?: any;
	sessionid?: string;

	/** @hidden */
	deferred: Deferred;

	constructor(client_req_id: string, uri: string, body?: any) {
		this.client_req_id = client_req_id;
		this.body = body;
		this.uri = uri;
		this.deferred = new Deferred();
	}
}

export class ECFReqMessage extends ECFMessage {

	method: MethodType;
	action?: string;
	timer?: any;

	constructor(method: MethodType, client_req_id: string, uri: string, sessionid?: string, body?: any, action?: string) {
		Validate([
			{ name: "method", value: method, enum: MethodType },
			{ name: "client_req_id", value: client_req_id },
			{ name: "uri", value: uri, type: Type.string },
		], "ECFReqMessage");
		super(client_req_id, uri, body);
		this.method = method;
		this.uri = uri;
		this.sessionid = sessionid;
		this.action = action;
	}

	print(): string {
		const message: any = { method: this.method, client_req_id: this.client_req_id, uri: this.uri, sessionid: this.sessionid, action: this.action, body: this.body };
		return JSON.stringify(message);
	}
}

export class ECFRespMessage extends ECFMessage {

	status_code: ResultCode;

	constructor(status_code: ResultCode, cliend_req_id?: string, body?: any) {
		Validate([
			{ name: "status_code", value: status_code, enum: ResultCode },
		], "ECFRespMessage");
		super(cliend_req_id, body);
		this.status_code = status_code;
	}

	print(): string {
		const message: any = { status_code: this.status_code, client_req_id: this.client_req_id, body: this.body };
		return JSON.stringify(message);
	}

}

/**
 * Enumeration of the possible HTTP methods that a single Request can have + ECF specific SUBSCRIBE/UNSUBSCRIBE methods.
 */
export enum MethodType {
	Get = "GET",
	Put = "PUT",
	Post = "POST",
	Delete = "DELETE",
	Subscribe = "SUBSCRIBE",
	Unsubscribe = "UNSUBSCRIBE"
}

enum EcfRTCState {
	On = "on",
	Off = "off",
	Signaling = "signaling",
	Error = "error",
}
